/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * LessonVideoUploadModal — mobile-first modal for kicking off an AI lesson
 * review upload from inside Work Mode.
 *
 * Pre-fills pupil + lesson date when launched from a pupil context. The
 * instructor confirms (or overrides) and picks a review type before
 * uploading. After upload, routes to /lesson-review?id=... so the
 * pricing/payment flow continues in the existing surface — no
 * duplicated payment system here.
 *
 * Review types are informational (the backend pricing endpoint owns the
 * exact options + prices). They map to the three visible tiers:
 *   • progress_clip — short shareable feedback clip
 *   • premium_review — full AI report
 *   • verified_progress_pack — premium + verified-progress badge
 *
 * Mobile-first: full-screen on phones, centered modal on desktop. The
 * primary action is a thumb-sized button at the bottom of the sheet.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Video, X, Upload, Sparkles, Calendar, User as UserIcon, Loader2,
  CheckCircle2, FileVideo,
} from "lucide-react";
import { MobileDialog } from "../mobile";
import usePickerHandoff from "../../hooks/usePickerHandoff";
import PickerRecoveryCallout from "../upload/PickerRecoveryCallout";
import { cloudUpload, CloudConfigError } from "../../lib/cloudUpload";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

const REVIEW_TYPES = [
  {
    id: "progress_clip",
    label: "Progress Clip",
    blurb: "Short shareable feedback clip — for the learner Passport.",
    Icon: Video,
  },
  {
    id: "premium_review",
    label: "Premium AI Review",
    blurb: "Full AI report: skills demonstrated, missed, and next focus.",
    Icon: Sparkles,
  },
  {
    id: "verified_progress_pack",
    label: "Verified Progress Pack",
    blurb: "Premium review plus a verified-progress badge for the Passport.",
    Icon: CheckCircle2,
  },
];

function isoToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function LessonVideoUploadModal({
  pupilId,
  pupilName,
  lessonId,
  requestId,
  onClose,
  onUploaded,
}) {
  // Body-scroll lock + ESC dismiss handled by <MobileDialog> primitive.
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [reviewType, setReviewType] = useState("premium_review");
  const [lessonDate, setLessonDate] = useState(isoToday());
  const [pupilNameLocal, setPupilNameLocal] = useState(pupilName || "");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  // P0 May 20, 2026 — universal picker handoff recovery. Detects
  // the "iPhone Photos handoff fired but onChange never returned"
  // failure mode and surfaces a visible recoverable callout so the
  // user is never dumped back to a dead start screen.
  const picker = usePickerHandoff({
    source: "lesson_video_upload_modal",
    hasStagedFiles: () => !!file,
  });

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      picker.markFilesStaged(1);
    }
  };

  const submit = async () => {
    if (!MEDIA_FEATURE_ENABLED) return;       // Quarantine — never POST.
    if (!file) {
      picker.openPicker();
      return;
    }
    setSubmitting(true);
    setProgress(0);
    try {
      // Cache the instructor's review-type pick for the lesson-review
      // page (pricing/checkout step happens there).
      try {
        localStorage.setItem(
          "wm_lesson_review_pref",
          JSON.stringify({ review_type: reviewType, ts: Date.now() })
        );
      } catch { /* localStorage may be disabled */ }

      // P0 May 24, 2026 — R2-native lesson_review upload. Replaces
      // the prior `axios.post('/api/lesson-review/upload', FormData)`
      // single-shot multipart POST, which streamed the whole file
      // through the FastAPI process and wrote it to /app local
      // disk. Under multi-user load that path would have exhausted
      // the preview pod disk. The R2-native flow puts the bytes
      // directly into Cloudflare R2 via signed multipart PUTs:
      //
      //   1. cloudUpload posts /api/uploads/cloud/init (R2 multipart create)
      //   2. cloudUpload PUTs each part directly to R2
      //   3. cloudUpload posts /api/uploads/cloud/{upload_id}/complete
      //      (R2 multipart finalize) — returns key + bucket.
      //   4. cloudUpload posts our completeUrl
      //      (/api/lesson-review/cloud-complete) with the canonical
      //      key + the lesson-context metadata. The backend creates
      //      the lesson_reviews row and kicks off processing.
      //
      // No /app disk write at any stage. No silent fallback to
      // chunked — CloudConfigError is terminal per operator doctrine.
      const completeBody = {};
      if (pupilId) completeBody.pupil_id = pupilId;
      if (pupilNameLocal) completeBody.pupil_name = pupilNameLocal;
      if (lessonId) completeBody.lesson_id = lessonId;
      if (lessonDate) completeBody.lesson_date = lessonDate;
      if (requestId) completeBody.request_id = requestId;

      const data = await cloudUpload({
        file,
        purpose: "lesson_review",
        context: {},
        completeUrl: `/api/lesson-review/cloud-complete`,
        completeBody,
        onProgress: ({ pct }) => {
          if (typeof pct === "number") setProgress(Math.round(pct));
        },
      });
      onUploaded?.(data);
      navigate(`/lesson-review?id=${data.review.id}`);
    } catch (e) {
      if (e instanceof CloudConfigError) {
        // No silent fallback. Operator doctrine: this MUST surface
        // verbatim so it can be diagnosed and fixed at the
        // infrastructure layer.
        toast.error(
          "Cloud upload is not configured. Please contact support — "
            + "do not retry on the local path."
        );
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Media quarantine — silent. Modal is a dead-end for non-admins
  // while /api/lesson-review/* + /api/uploads/cloud/* return 503.
  if (!MEDIA_FEATURE_ENABLED) return null;

  return (
    <MobileDialog
      bareContent
      open
      onClose={onClose}
      testid="wm-upload-modal"
      priority="urgent"
    >
      <div
        className="w-full sm:max-w-md bg-slate-900 sm:rounded-2xl rounded-t-3xl border-t sm:border border-slate-700"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Video className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300">
                AI Lesson Review
              </p>
              <h3 className="text-base font-extrabold text-white leading-tight">
                Upload Lesson Video
              </h3>
            </div>
          </div>
          <button
            data-testid="wm-upload-modal-close"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5"
            disabled={submitting}
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Pupil */}
          <div>
            <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5">
              <UserIcon className="w-3 h-3 inline mr-1" /> Pupil
            </label>
            {pupilId ? (
              <div
                data-testid="wm-upload-pupil-prefilled"
                className="rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm font-bold text-white"
              >
                {pupilNameLocal || "Pupil"}
                <span className="ml-2 text-[10px] font-bold text-emerald-400">
                  · pre-filled from lesson
                </span>
              </div>
            ) : (
              <input
                data-testid="wm-upload-pupil-input"
                type="text"
                value={pupilNameLocal}
                onChange={(e) => setPupilNameLocal(e.target.value)}
                placeholder="Pupil name (optional)"
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white placeholder-slate-500"
              />
            )}
          </div>

          {/* Lesson date */}
          <div>
            <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5">
              <Calendar className="w-3 h-3 inline mr-1" /> Lesson date
            </label>
            <input
              data-testid="wm-upload-lesson-date"
              type="date"
              value={lessonDate}
              onChange={(e) => setLessonDate(e.target.value)}
              className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white"
            />
          </div>

          {/* Review type */}
          <div>
            <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5">
              Review type
            </label>
            <div className="space-y-1.5">
              {REVIEW_TYPES.map((rt) => {
                const selected = reviewType === rt.id;
                return (
                  <button
                    key={rt.id}
                    type="button"
                    data-testid={`wm-upload-review-type-${rt.id}`}
                    onClick={() => setReviewType(rt.id)}
                    className={`w-full text-left rounded-lg p-3 ring-2 transition flex items-start gap-2.5 ${
                      selected
                        ? "ring-indigo-500 bg-indigo-500/15"
                        : "ring-transparent bg-slate-800 hover:bg-slate-700"
                    }`}
                  >
                    <rt.Icon
                      className={`w-4 h-4 flex-none mt-0.5 ${
                        selected ? "text-indigo-300" : "text-slate-400"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-[13px] font-extrabold ${
                          selected ? "text-white" : "text-slate-200"
                        }`}
                      >
                        {rt.label}
                      </p>
                      <p className="text-[11.5px] text-slate-400 leading-snug mt-0.5">
                        {rt.blurb}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5">
              Free for instructors — processing starts immediately, no payment.
            </p>
          </div>

          {/* File picker */}
          <div>
            <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5">
              Lesson footage
            </label>
            {picker.recoveryHint && (
              <div className="mb-3">
                <PickerRecoveryCallout
                  source="lesson_video_upload_modal"
                  onRePick={() => { picker.clearRecovery(); picker.openPicker(); }}
                  onDismiss={picker.clearRecovery}
                  testid="wm-upload-recovery-callout"
                />
              </div>
            )}
            <input
              ref={picker.inputRef}
              type="file"
              accept="video/*,audio/*,.mp4,.mov,.mp3,.m4a,.wav"
              onChange={picker.wrapOnChange(onPick)}
              className="hidden"
              data-testid="wm-upload-file-input"
            />
            <button
              type="button"
              data-testid="wm-upload-file-pick"
              onClick={() => picker.openPicker()}
              className={`w-full rounded-xl border-2 border-dashed transition px-4 py-5 flex items-center gap-3 ${
                file
                  ? "border-emerald-500/60 bg-emerald-500/10"
                  : "border-slate-600 bg-slate-800/50 hover:bg-slate-800"
              }`}
              disabled={submitting}
            >
              <FileVideo
                className={`w-6 h-6 flex-none ${
                  file ? "text-emerald-300" : "text-slate-400"
                }`}
              />
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[13px] font-extrabold text-white truncate">
                  {file ? file.name : "Choose video or audio file"}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {file
                    ? `${(file.size / 1024 / 1024).toFixed(1)} MB · tap to change`
                    : "MP4, MOV, MP3, M4A or WAV — up to 500 MB"}
                </p>
              </div>
            </button>
          </div>

          {/* Progress */}
          {submitting && (
            <div data-testid="wm-upload-progress">
              <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
                <span>Uploading…</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Sticky CTA */}
        <div className="px-5 py-4 border-t border-slate-800 flex gap-2 sticky bottom-0 bg-slate-900">
          <button
            data-testid="wm-upload-modal-cancel"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-12 rounded-xl bg-slate-800 text-slate-200 font-bold text-sm hover:bg-slate-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="wm-upload-modal-submit"
            onClick={submit}
            disabled={submitting || !file}
            className="flex-[2] h-12 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-extrabold text-sm transition flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" /> Upload &amp; continue
              </>
            )}
          </button>
        </div>
      </div>
    </MobileDialog>
  );
}
